import React, { useEffect, useState, useReducer, useRef } from "react";
import { observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import PaginationComponent from "Components/common/PaginationComponent";
import CustomTooltip from "Components/common/CustomTooltip";
import ImportProgressbar from "Components/common/ImportProgressbar";
import { MXP_EVENT } from "Static/MixPanelEvents";
import Utils from "Utils/utils";
import { BULK_TASKS_OPERATION_DATE } from "Static/constant";
import { BULK_TASK_TYPE, UPDATE_TASK_READ_STATUS_LABEL } from "Model/model";
import makeApi, { URLS } from "Utils/apiURL";
import bulkJobsNotificationStore from "Stores/bulkJobsNotification";
import InboxImportRowDetail from "./InboxImportRowDetail";
import { ReactComponent as ChevronDownIcon } from "Assets/svg/bulkCsvConnect/chevronDown.svg";

const INBOX_IMPORT_TYPE = "CONNECT_INBOX_CSV";
const HIDDEN_JOB_TYPES = ["SEQUENCE_INBOX_REBALANCE"];
const visibleJobs = (jobs = []) => jobs.filter(j => !HIDDEN_JOB_TYPES.includes(j?.type));

const initialState = {
  logs: {},
  pageNo: 1,
  showTooltip: false,
  loader: true
};

const logReducer = (state = {}, action = () => { }) => {
  switch (action?.type) {
    case "UPDATE_LOG_STATE":
      return {
        ...state,
        ...action.payload
      };
    default:
      return state;
  }
};

const TasksOperations = observer(
  withRouter(() => {
    const [logState, dispatch] = useReducer(logReducer, initialState);
    const [inboxRowsByJobId, setInboxRowsByJobId] = useState({});
    const totalPages = logState?.logs?.totalPages;
    const updateLogState = (newState = {}) => {
      dispatch({
        type: "UPDATE_LOG_STATE",
        payload: newState
      });
    };

    const statusField = {
      TO_BE_PROCESSED: "Pending",
      IN_PROGRESS: "In Progress",
      PROCESSED: "Completed",
      FAILED: "Failed"
    };

    const headerList = [
      { name: "Action", key: "type" },
      { name: "Progress", key: "progress" },
      { name: "Status", key: "status" },
      { name: "Total", key: "totalCount" },
      { name: "Success", key: "successCount" },
      { name: "Failed", key: "failedCount" },
      { name: "Created", key: "createdDate" },
      { name: "Owner", key: "ownerName" }
    ];

    // Fetches every inbox-import job's per-row detail (email/provider/reason) up front, as soon
    // as the log list itself loads, instead of lazily on row expand - so "Loading skipped
    // inboxes..." never shows on click, the rows are already there. Runs in parallel across jobs.
    // A job already fetched is skipped on later poll ticks unless it's still in progress (whose
    // rows can still change), so a 5s poll doesn't re-hit every completed job's row endpoint.
    const fetchedJobIdsRef = useRef(new Set());
    const prefetchInboxImportRows = jobs => {
      const inboxJobs = (jobs || []).filter(
        j =>
          j?.type === INBOX_IMPORT_TYPE &&
          j?.id &&
          (!fetchedJobIdsRef.current.has(j.id) ||
            ["TO_BE_PROCESSED", "IN_PROGRESS"].includes(j.status))
      );
      inboxJobs.forEach(async job => {
        fetchedJobIdsRef.current.add(job.id);
        const config = {
          url: (URLS.connectInboxCsvRows || "").replace("<<jobId>>", job.id),
          method: "GET"
        };
        const res = await makeApi(config);
        setInboxRowsByJobId(prev => ({
          ...prev,
          [job.id]: Array.isArray(res?.data) ? res.data : []
        }));
      });
    };

    const getTasksOperationLogs = async (pageNo = 1) => {
      try {
        const config = {
          url: URLS.tasksOperationLogs || "",
          method: "POST",
          data: { limit: 10, pageNo, date: BULK_TASKS_OPERATION_DATE }
        };
        const res = await makeApi(config);
        const { jobs: rawJobs = [], totalCount = 0 } = res?.data || {};
        const jobs = visibleJobs(rawJobs);
        if (res?.data) {
          if (totalCount > 0 && jobs?.length > 0) {
            updateLogState({
              ...logState,
              logs: { ...res.data, jobs },
              loader: false
            });
            prefetchInboxImportRows(jobs);
            const hasInProgress = jobs.some(j =>
              ["TO_BE_PROCESSED", "IN_PROGRESS"].includes(j.status)
            );
            return hasInProgress;
          } else if (res?.response?.status === 401) {
            Utils.showLoggedOutPopup();
          }
        } else {
          Utils.showApiFailureResponse(res);
        }
        updateLogState({ ...logState, loader: false });
        return false;
      } catch (err) {
        console.error("Fetching bulk tasks log error", err);
        return false;
      }
    };

    const paginate = (obj = {}) => {
      if (!logState?.loader) {
        let pageNo = obj?.selected + 1 || 1;
        stopPolling();
        updateLogState({ ...logState, pageNo, loader: true });
        getTasksOperationLogs(pageNo).then(hasInProgress => {
          if (hasInProgress) startPolling();
        });
      }
    };

    const getSplittedFailedObj = (FailureCounts = {}) => {
      let skipCount = 0;
      let manualEmailCount = 0;
      let phoneCallCount = 0;
      if (FailureCounts && Object.keys(FailureCounts)?.length > 0) {
        for (const property in FailureCounts) {
          const value = FailureCounts?.[property] || 0;
          if ([101, "101"].includes(property)) {
            skipCount = value;
          } else if ([102, "102"].includes(property)) {
            manualEmailCount = value;
          } else if ([103, "103"].includes(property)) {
            phoneCallCount = value;
          }
        }
      }
      return {
        skipCount,
        manualEmailCount,
        phoneCallCount
      };
    };

    const pollIntervalRef = useRef(null);

    const startPolling = () => {
      if (pollIntervalRef.current) return;
      pollIntervalRef.current = setInterval(async () => {
        const config = {
          url: URLS.tasksOperationLogs || "",
          method: "POST",
          data: { limit: 10, pageNo: logState?.pageNo || 1, date: BULK_TASKS_OPERATION_DATE }
        };
        const res = await makeApi(config);
        if (res?.data) {
          const jobs = visibleJobs(res.data?.jobs || []);
          updateLogState({ logs: { ...res.data, jobs }, loader: false });
          prefetchInboxImportRows(jobs);
          const hasInProgress = jobs.some(j =>
            ["TO_BE_PROCESSED", "IN_PROGRESS"].includes(j.status)
          );
          if (!hasInProgress) stopPolling(true);
        }
      }, 5000);
    };

    const stopPolling = (completed = false) => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (completed) {
          bulkJobsNotificationStore.fetchJobs();
          bulkJobsNotificationStore.triggerGuide("Your bulk action job is completed.");
        }
      }
    };

    useEffect(() => {
      updateLogState({ ...initialState, loader: true });
      Utils.mixpanelTrack(MXP_EVENT.TASKS_BULK_OPERATION_VIEWED);
      getTasksOperationLogs().then(hasInProgress => {
        if (hasInProgress) startPolling();
      });
      return () => {
        stopPolling();
        updateLogState({ ...initialState });
        fetchedJobIdsRef.current = new Set();
        setInboxRowsByJobId({});
      };
    }, []);

    return (
      <div className="tasksOperation">
        <div className="titleHeader">
          <h3>Bulk Actions</h3>
        </div>
        <div className="tableList">
          <div className="tableHeader">
            {headerList?.map((item, index) => {
              const { name = "", key = "" } = item || {};
              return (
                <div
                  className={`titleRow ${key}`}
                  key={`tasksOperationTitle${index}`}
                >
                  {name}
                </div>
              );
            })}
          </div>
          {!logState?.loader ? (
            <BulkActionLogsTable
              logState={logState}
              statusField={statusField}
              getSplittedFailedObj={getSplittedFailedObj}
              updateLogState={updateLogState}
              inboxRowsByJobId={inboxRowsByJobId}
            />
          ) : (
            <LoaderComp headerList={headerList} />
          )}
        </div>
        <PaginationComponent
          callback={paginate}
          loading={logState?.loader}
          totalPages={totalPages}
          currentPage={logState?.logs?.currentPage || 1}
        />
      </div>
    );
  })
);

const BulkActionLogsTable = (props = {}) => {
  const {
    logState = [],
    statusField = {},
    getSplittedFailedObj = () => { },
    updateLogState = () => { },
    inboxRowsByJobId = {}
  } = props || {};

  const [expandedJobId, setExpandedJobId] = useState(null);

  const getDate = (createTs = "") => {
    return createTs ? Utils.getDateFromString(createTs || "--") : "--";
  };

  return (
    <>
      {(logState?.logs?.jobs?.length > 0 && (
        <div className="tableRecordHeightAlign">
          {logState?.logs?.jobs?.map((item, index) => {
            const {
              id = "",
              FailureCounts = {},
              status = "",
              type = "",
              createTs = "",
              owner = {},
              totalCount = 0,
              successCount = 0,
              failedCount = 0,
              readUnreadJobType = "",
              failedContactsData = {},
              successContactsData = {},
              totalContactsData = {},
              totalContacts = 0,
              processedContacts = 0
            } = item || {};

            const isReconnectInbox = type === "RECONNECT_INBOX";
            const isInboxImport = type === INBOX_IMPORT_TYPE;

            const isInProgress = status === "IN_PROGRESS";
            const isPending = status === "TO_BE_PROCESSED";
            const inProgress = isInProgress || isPending;
            const isExpanded = isInboxImport && expandedJobId === id;

            const toggleExpanded = () => {
              if (isInboxImport) {
                setExpandedJobId(prev => (prev === id ? null : id));
              }
            };

            return (
              <React.Fragment key={`tasksOperationLogs${index}`}>
                <div
                  className={`tableBody ${id} ${isInboxImport ? "inboxImportRow" : ""} ${isExpanded ? "expanded" : ""}`}
                  onClick={toggleExpanded}
                >
                  <div className={`type titleRow`}>
                    {type === "UPDATE_TASK_READ_STATUS"
                      ? UPDATE_TASK_READ_STATUS_LABEL[readUnreadJobType || ""]
                      : BULK_TASK_TYPE[type]}
                    {isInboxImport && (
                      <span className={`inboxImportRowChevron ${isExpanded ? "open" : ""}`}>
                        <ChevronDownIcon />
                      </span>
                    )}
                  </div>
                  <div className={`progress titleRow`}>
                    {isInProgress ? (
                      <ImportProgressbar total={totalContacts} processed={processedContacts} />
                    ) : (
                      "--"
                    )}
                  </div>
                  <div className={`status titleRow`}>
                    {statusField[status] || "--"}
                  </div>
                  <div className={`totalCount titleRow`}>
                    {inProgress ? (
                      "--"
                    ) : isReconnectInbox &&
                      totalContactsData &&
                      Object.keys(totalContactsData).length > 0 ? (
                      <MapGenericComp
                        data={totalContactsData}
                        total={totalCount}
                        title={`Total Contacts (${totalCount})`}
                      />
                    ) : (
                      totalCount
                    )}
                  </div>
                  <div className={`successCount titleRow`}>
                    {inProgress ? (
                      "--"
                    ) : isReconnectInbox &&
                      successContactsData &&
                      Object.keys(successContactsData).length > 0 ? (
                      <MapGenericComp
                        data={successContactsData}
                        total={successCount}
                        title={`Total Contacts (${successCount})`}
                      />
                    ) : (
                      successCount
                    )}
                  </div>
                  <div className={`failedCount titleRow`}>
                    {inProgress ? (
                      "--"
                    ) : isReconnectInbox &&
                      failedContactsData &&
                      Object.keys(failedContactsData).length > 0 ? (
                      <MapGenericComp
                        data={failedContactsData}
                        total={failedCount}
                        title={`Total Contacts (${failedCount})`}
                      />
                    ) : (
                      (failedCount > 0 && (
                        <FailureCountsComp
                          {...getSplittedFailedObj(FailureCounts)}
                          total={failedCount}
                          isInboxImport={isInboxImport}
                        />
                      )) ||
                      0
                    )}
                  </div>
                  <div className={`createdDate titleRow`}>
                    {getDate(createTs)}
                  </div>
                  <div className={`ownerName titleRow`}>
                    {(owner && (
                      <div
                        className={`contactOwner`}
                        onMouseEnter={e => {
                          e.stopPropagation();
                          updateLogState({ ...logState, showTooltip: id });
                        }}
                        onMouseLeave={e => {
                          e.stopPropagation();
                          updateLogState({
                            ...logState,
                            showTooltip: false
                          });
                        }}
                      >
                        <span className="circleOwner">
                          {Utils.getOwnerInitial(owner)}
                        </span>
                        {logState?.showTooltip === id && (
                          <CustomTooltip
                            text={`Added by ${Utils.getOwnerName(owner)}`}
                          />
                        )}
                      </div>
                    )) ||
                      "--"}
                  </div>
                </div>
                {isExpanded && (
                  <div className="inboxImportDetailWrapper">
                    <InboxImportRowDetail
                      job={item}
                      rows={inboxRowsByJobId[id]}
                      loading={inboxRowsByJobId[id] === undefined}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )) || (
          <div className="noDataFound">
            <i className="material-icons-outlined">list_alt</i>
            <span className="text">No bulk actions found</span>
          </div>
        )}
    </>
  );
};

const FailureCountsComp = ({
  skipCount = 0,
  manualEmailCount = 0,
  phoneCallCount = 0,
  total = 0,
  isInboxImport = false
}) => {
  const [showTooltip, setTooltip] = useState(false);
  const [styleCss, setStyleCss] = useState();

  const toggleTooltip = (event = "") => {
    let domPositionY =
      event?.nativeEvent?.layerY || event?.nativeEvent.offsetY || 0;
    let diffHeight = window.innerHeight - event?.clientY || event?.pageY;
    let styleObject = {};
    if (diffHeight - domPositionY + event?.currentTarget?.clientHeight >= 144) {
      styleObject.top =
        event?.clientY -
        event?.nativeEvent.layerY +
        event?.currentTarget?.clientHeight;
    } else {
      styleObject.bottom = diffHeight + event?.nativeEvent?.layerY;
    }
    styleObject.right = window.innerWidth - event?.clientX;
    setStyleCss(styleObject);
    setTooltip(true);
  };

  const hideTooltip = () => {
    setStyleCss({});
    setTooltip(false);
  };

  return (
    <div className="tasksFailedOperation">
      <span
        className="count"
        onMouseOver={event => (total > 0 && toggleTooltip(event, true)) || {}}
        onMouseOut={() => (total > 0 && hideTooltip(false)) || {}}
      >
        {total}
      </span>
      {showTooltip && (
        <div className="cardTooltip animatedFast" style={styleCss}>
          <div className="cardTitle">
            {isInboxImport
              ? `Failed to connect ${total} inbox${total > 1 ? "es" : ""}`
              : `Failed to upload ${total} contact${total > 1 ? "s" : ""}`}
          </div>
          <div className="cardBody">
            {(skipCount && (
              <div className="detailedInfoCount">
                <span className="material-icons-outlined listIcon">
                  radio_button_checked
                </span>
                <span className="text">
                  {skipCount} Skip task{skipCount > 1 ? "s" : ""} for reply
                  thread
                </span>
              </div>
            )) ||
              ""}
            {(phoneCallCount && (
              <div className="detailedInfoCount">
                <span className="material-icons-outlined listIcon">
                  radio_button_checked
                </span>
                <span className="text">
                  {phoneCallCount} phone call task
                  {phoneCallCount > 1 ? "s" : ""}
                </span>
              </div>
            )) ||
              ""}
            {(manualEmailCount && (
              <div className="detailedInfoCount">
                <span className="material-icons-outlined listIcon">
                  radio_button_checked
                </span>
                <span className="text">
                  {manualEmailCount} manual email task
                  {manualEmailCount > 1 ? "s" : ""}
                </span>
              </div>
            )) ||
              ""}
          </div>
        </div>
      )}
    </div>
  );
};

const MapGenericComp = ({ data = {}, total = 0, title = "" }) => {
  const [showTooltip, setTooltip] = useState(false);
  const [styleCss, setStyleCss] = useState();

  const toggleTooltip = (event = "") => {
    let domPositionY =
      event?.nativeEvent?.layerY || event?.nativeEvent.offsetY || 0;
    let diffHeight = window.innerHeight - event?.clientY || event?.pageY;
    let styleObject = {};
    if (diffHeight - domPositionY + event?.currentTarget?.clientHeight >= 144) {
      styleObject.top =
        event?.clientY -
        event?.nativeEvent.layerY +
        event?.currentTarget?.clientHeight;
    } else {
      styleObject.bottom = diffHeight + event?.nativeEvent?.layerY;
    }
    styleObject.right = window.innerWidth - event?.clientX;
    setStyleCss(styleObject);
    setTooltip(true);
  };

  const hideTooltip = () => {
    setStyleCss({});
    setTooltip(false);
  };

  return (
    <div className="tasksFailedOperation">
      <span
        className="count"
        onMouseOver={event => (total > 0 && toggleTooltip(event)) || {}}
        onMouseOut={() => (total > 0 && hideTooltip()) || {}}
      >
        {total}
      </span>
      {showTooltip && (
        <div className="cardTooltip animatedFast" style={styleCss}>
          {title && <div className="cardTitle">{title}</div>}
          <div className="cardBody">
            {Object.keys(data).map((key, index) => (
              <div
                className="detailedInfoCount"
                key={`detailedData${index}`}
                style={{ display: "flex", alignItems: "center" }}
              >
                <span
                  className="material-icons-outlined listIcon"
                  style={{ fontSize: "8px", color: "#000" }}
                >
                  fiber_manual_record
                </span>
                <span className="text">
                  {key}: {Array.isArray(data[key]) ? data[key].length : 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LoaderComp = (props = {}) => {
  const { headerList = [] } = props || {};
  return (
    <div className="adjustHt">
      {Array.from({ length: 8 }, (value, index) => (
        <div className="tableBodyLoader" key={`tableBodyLoader${index + 1}`}>
          {headerList?.length &&
            headerList?.map((item, idx) => {
              const { key = "" } = item || {};
              return (
                <div className={`${key}`} key={`${key}${index}${idx}`}>
                  <span className={`colLoader linear-background`} />
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
};
export default TasksOperations;
